<template>
  <div class="preview-page">
    <header class="preview-header">
      <div>
        <p class="eyebrow">Test Preview</p>
        <h1>{{ previewStory?.title || '绘本预览' }}</h1>
        <p class="summary">
          {{ previewStory?.theme || '暂无故事数据，请先从一键创作完成生成。' }}
        </p>
      </div>
      <button type="button" @click="router.push({ name: 'create' })">返回创作</button>
    </header>

    <main v-if="previewStory" class="preview-content">
      <section class="panel">
        <div class="section-title">
          <h2>角色形象</h2>
          <span>{{ characterPreviews.length }} 个角色形象</span>
        </div>
        <div class="character-grid">
          <article
            v-for="character in characterPreviews"
            :key="character.id"
            class="character-card"
          >
            <div class="image-box character-image">
              <img v-if="character.imageUrl" :src="character.imageUrl" :alt="character.name" />
              <div v-else class="empty-image">暂无角色图</div>
            </div>
            <div class="character-copy">
              <h3>{{ character.name }}</h3>
              <p>{{ character.description }}</p>
            </div>
          </article>
        </div>
      </section>

      <section class="panel">
        <div class="section-title">
          <h2>封面</h2>
          <span>{{ coverPreview.imageUrl ? '已生成' : '暂无图片' }}</span>
        </div>
        <div class="cover-card">
          <div class="image-box cover-image">
            <img v-if="coverPreview.imageUrl" :src="coverPreview.imageUrl" alt="封面" />
            <div v-else class="empty-image">暂无封面图</div>
          </div>
          <div class="cover-copy">
            <p>{{ coverPreview.description }}</p>
            <div v-if="coverPreview.characters.length" class="cover-character-list">
              <h3>封面角色</h3>
              <div class="cover-character-tags">
                <span
                  v-for="character in coverPreview.characters"
                  :key="character.id"
                  class="cover-character-tag"
                >
                  {{ character.name }}
                  <em v-if="character.expression">{{ character.expression }}</em>
                  <em v-if="character.position">{{ character.position }}</em>
                  <em v-if="character.action">{{ character.action }}</em>
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="panel">
        <div class="section-title">
          <h2>页面图片</h2>
          <span>{{ previewStory.pages?.length || 0 }} 页</span>
        </div>
        <div class="page-grid">
          <article v-for="page in previewPages" :key="page.page" class="page-card">
            <div class="image-box page-image">
              <img v-if="page.image_url" :src="page.image_url" :alt="`第${page.page}页`" />
              <div v-else class="empty-image">暂无页面图</div>
            </div>
            <div class="page-copy">
              <strong>P:{{ String(page.page).padStart(2, '0') }}</strong>
              <p class="narration">{{ page.narration }}</p>
              <p class="scene">
                {{ page.scene_description || page.scene_semantic?.action || '暂无画面描述' }}
              </p>
            </div>
          </article>
        </div>
      </section>
    </main>

    <div v-else class="empty-state">
      <h2>暂无预览数据</h2>
      <p>请先完成第三步生成绘本，再点击“阅读绘本”。</p>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useRouter } from 'vue-router';
  import {
    resolveStorybookImageUrl,
    type ImageGenerateResult,
    type StoryAppearance,
    type StoryBook,
    type StoryCharacter,
    type StoryCoverCharacter,
    type StorybookFileContext
  } from '@/api/storybook';

  interface CharacterPreview {
    id: string;
    name: string;
    description: string;
    imageUrl: string;
  }

  interface PreviewStoragePayload {
    story?: StoryBook;
    result?: ImageGenerateResult;
    uLink?: string;
    linkName?: string;
  }

  const router = useRouter();

  const raw = sessionStorage.getItem('storybook:test-preview');
  const previewData = raw ? (JSON.parse(raw) as PreviewStoragePayload) : null;
  const fileContext: StorybookFileContext = {
    uLink: previewData?.uLink,
    linkName: previewData?.linkName
  };
  const resolveImageUrl = (url?: string) => resolveStorybookImageUrl(url ?? '', fileContext);
  const imageResult = computed(() => previewData?.result ?? null);
  const previewStory = computed(() => previewData?.result?.story ?? previewData?.story ?? null);

  const truncateText = (value: string, maxLength = 86) => {
    if (value.length <= maxLength) return value;
    return `${value.slice(0, maxLength)}...`;
  };

  const buildAppearanceDescription = (appearance?: StoryAppearance) => {
    if (!appearance) return '';
    return [
      appearance.age_look,
      appearance.hair,
      appearance.eyes,
      appearance.skin,
      appearance.build,
      appearance.outfit,
      appearance.distinctive_feature
    ]
      .filter(Boolean)
      .join('，');
  };

  const buildCharacterDescription = (character: StoryCharacter) =>
    truncateText(
      buildAppearanceDescription(character.appearance) || character.personality || '暂无角色描述'
    );

  const buildFormCharacterPreviews = (character: StoryCharacter): CharacterPreview[] =>
    (character.forms ?? []).map((formItem) => ({
      id: `${character.id}-${formItem.id}`,
      name: `${character.name}-${formItem.name}`,
      imageUrl: resolveImageUrl(
        formItem.reference_image_url || formItem.input_reference_image_url || ''
      ),
      description: truncateText(buildAppearanceDescription(formItem.appearance) || '暂无形态描述')
    }));

  const characterPreviews = computed<CharacterPreview[]>(() =>
    (previewStory.value?.characters ?? []).flatMap((character) => {
      if (character.forms?.length) return buildFormCharacterPreviews(character);
      return [
        {
          id: character.id,
          name: character.name,
          imageUrl: resolveImageUrl(
            character.reference_image_url || character.input_reference_image_url || ''
          ),
          description: buildCharacterDescription(character)
        }
      ];
    })
  );

  const previewPages = computed(() =>
    (previewStory.value?.pages ?? []).map((page) => ({
      ...page,
      image_url: resolveImageUrl(page.image_url)
    }))
  );

  const coverPreview = computed(() => {
    const story = previewStory.value;
    const resultCover = imageResult.value?.cover;
    const storyCover = story?.cover;
    const namedCharacters = storyCover?.named_characters ?? [];
    const characterIds = resultCover?.character_ids ?? storyCover?.character_ids ?? [];
    const characters: StoryCoverCharacter[] = namedCharacters.length
      ? namedCharacters
      : characterIds.map((id) => ({ id, action: '', position: '', expression: '' }));

    return {
      imageUrl: resolveImageUrl(resultCover?.image_url || ''),
      description:
        resultCover?.description ||
        storyCover?.description ||
        story?.cover_description ||
        resultCover?.prompt ||
        '暂无封面描述',
      characters: characters.map((coverCharacter) => {
        const character = story?.characters?.find((item) => item.id === coverCharacter.id);
        return {
          ...coverCharacter,
          name: character?.name || coverCharacter.id
        };
      })
    };
  });
</script>

<style scoped>
  .preview-page {
    width: 100%;
    height: 100vh;
    overflow-y: auto;
    padding: 40px;
    box-sizing: border-box;
    background: #fbf9ff;
    color: #160211;
  }

  .preview-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    max-width: 1280px;
    margin: 0 auto 28px;
  }

  .eyebrow {
    margin: 0 0 8px;
    color: #6033e1;
    font-size: 13px;
    font-weight: 800;
    text-transform: uppercase;
  }

  .preview-header h1 {
    margin: 0;
    font-size: 30px;
  }

  .summary {
    max-width: 760px;
    margin: 12px 0 0;
    color: #6e6a7c;
    line-height: 1.7;
  }

  .preview-header button {
    height: 40px;
    padding: 0 18px;
    border: 0;
    border-radius: 12px;
    background: #6033e1;
    color: #fff;
    font-weight: 800;
    cursor: pointer;
  }

  .preview-content {
    max-width: 1280px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin: 0 auto;
  }

  .panel {
    padding: 22px;
    border-radius: 18px;
    background: #fff;
    box-shadow: 0 14px 40px rgba(73, 56, 130, 0.08);
  }

  .section-title {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 18px;
  }

  .section-title h2 {
    margin: 0;
    font-size: 20px;
  }

  .section-title span {
    color: #6e6a7c;
    font-size: 13px;
    font-weight: 700;
  }

  .character-grid,
  .page-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 18px;
  }

  .character-card,
  .page-card,
  .cover-card {
    border: 1px solid #eeeaf8;
    border-radius: 14px;
    overflow: hidden;
    background: #fff;
  }

  .character-card h3,
  .character-card p,
  .page-copy {
    padding: 0 14px;
  }

  .character-copy {
    padding-bottom: 14px;
    text-align: center;
  }

  .multi-form-character {
    grid-column: span 2;
  }

  .form-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 24px;
    margin-bottom: 8px;
    padding: 0 10px;
    border-radius: 999px;
    background: #f3efff;
    color: #6033e1;
    font-size: 12px;
    font-weight: 800;
  }

  .form-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 12px;
    padding: 0 14px 14px;
  }

  .form-card {
    overflow: hidden;
    border: 1px solid #eeeaf8;
    border-radius: 12px;
    background: #fbf9ff;
  }

  .form-image {
    height: 150px;
  }

  .form-copy {
    padding: 10px;
  }

  .form-copy strong {
    display: block;
    color: #160211;
    font-size: 13px;
  }

  .form-copy p {
    margin: 7px 0 0;
    padding: 0;
    font-size: 12px;
    line-height: 1.5;
    text-align: left;
  }

  .cover-copy {
    padding: 14px;
  }

  .character-card h3 {
    margin: 14px 0 8px;
    text-align: center;
  }

  .character-card p,
  .cover-copy p,
  .scene {
    color: #6e6a7c;
    line-height: 1.6;
  }

  .cover-copy p {
    margin: 0;
  }

  .cover-character-list {
    margin-top: 14px;
  }

  .cover-character-list h3 {
    margin: 0 0 10px;
    color: #160211;
    font-size: 15px;
  }

  .cover-character-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .cover-character-tag {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    padding: 7px 10px;
    border-radius: 999px;
    background: #f3efff;
    color: #6033e1;
    font-size: 12px;
    font-weight: 800;
  }

  .cover-character-tag em {
    color: #7d758f;
    font-style: normal;
    font-weight: 700;
  }

  .image-box {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f1ff;
  }

  .image-box img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .character-image {
    height: 260px;
  }

  .cover-image {
    height: 420px;
  }

  .page-image {
    height: 220px;
  }

  .empty-image {
    color: #a5a0af;
    font-weight: 800;
  }

  .page-copy {
    padding-block: 14px;
  }

  .page-copy strong {
    color: #6033e1;
  }

  .narration {
    color: #160211;
    font-weight: 700;
    line-height: 1.6;
  }

  .empty-state {
    max-width: 520px;
    margin: 140px auto;
    padding: 36px;
    border-radius: 18px;
    background: #fff;
    text-align: center;
    box-shadow: 0 14px 40px rgba(73, 56, 130, 0.08);
  }
  @media (max-width: 760px) {
    .preview-page {
      padding: 24px 16px;
    }

    .preview-header {
      flex-direction: column;
    }

    .multi-form-character {
      grid-column: span 1;
    }
  }
</style>
